import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/mlo/Dashboard";
import NewRun from "@/pages/mlo/NewRun";
import LeadWallet from "@/pages/mlo/LeadWallet";
import PaymentReturn from "@/pages/PaymentReturn";
import Prospects from "@/pages/mlo/Prospects";
import Leads from "@/pages/mlo/Leads";
import Communications from "@/pages/mlo/Communications";
import Profile from "@/pages/mlo/Profile";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminTemplates from "@/pages/admin/AdminTemplates";
import AdminSuppression from "@/pages/admin/AdminSuppression";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminApiUsage from "@/pages/admin/AdminApiUsage";
import AdminStripe from "@/pages/admin/AdminStripe";
import Respond from "@/pages/Respond";
import AdminPostcards from "@/pages/admin/AdminPostcards";
import { DisclaimerGate } from "@/components/DisclaimerGate";
import CallLog from "@/pages/CallLog";
import Appointments from "@/pages/Appointments";
import AdminAiScript from "@/pages/admin/AdminAiScript";
import AdminTextScript from "@/pages/admin/AdminTextScript";
import Texts from "@/pages/Texts";
import AdminDeitySubs from "@/pages/admin/AdminDeitySubs";
import AdminAstoria from "@/pages/admin/AdminAstoria";
import AdminMarketplace from "@/pages/admin/AdminMarketplace";
import AdminLiveTransfers from "@/pages/admin/AdminLiveTransfers";
import AdminSurety from "@/pages/admin/AdminSurety";
import SuretyRuns from "@/pages/surety/SuretyRuns";
import SuretyLeads from "@/pages/surety/SuretyLeads";
import SuretyProfile from "@/pages/surety/SuretyProfile";
import SuretyTexts from "@/pages/surety/SuretyTexts";
import SuretyCalls from "@/pages/surety/SuretyCalls";
import SuretyAppointments from "@/pages/surety/SuretyAppointments";
import BondThanks from "@/pages/BondThanks";
import Marketplace from "@/pages/mlo/Marketplace";
import CommunicationsHub from "@/pages/mlo/CommunicationsHub";
import Integrations from "@/pages/mlo/Integrations";
import PmLeads from "@/pages/pm/PmLeads";
import PmAppointments from "@/pages/pm/PmAppointments";
import PmEmails from "@/pages/pm/PmEmails";
import PmRecordings from "@/pages/pm/PmRecordings";
import PmTexts from "@/pages/pm/PmTexts";
import RtReo from "@/pages/realtor/RtReo";
import RtPayments from "@/pages/realtor/RtPayments";
import ReverseProspecting from "@/pages/ReverseProspecting";
import AdminRealtors from "@/pages/admin/AdminRealtors";
import PmMarket from "@/pages/pm/PmMarket";
import AdminPmMarket from "@/pages/admin/AdminPmMarket";
import AdminPM from "@/pages/admin/AdminPM";
import AdminRecordings from "@/pages/admin/AdminRecordings";
import Referrals from "@/pages/Referrals";
import SignAgreement from "@/pages/SignAgreement";
import UploadDocs from "@/pages/UploadDocs";

const homeFor = (u) => (u.role === "admin" ? "/admin" : u.role === "surety_agent" ? "/surety" : u.role === "property_manager" ? "/pm" : u.role === "realtor" ? "/realtor" : "/dashboard");

function Splash() {
  return (
    <div className="min-h-screen flex items-center justify-center navy-panel">
      <div className="text-amber-400 font-display text-2xl animate-pulse">Elite Leads</div>
    </div>
  );
}

function Protected({ children, admin, surety, pm, any }) {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (any) return children;
  if (user.role === "property_manager" && !pm) return <Navigate to="/pm" replace />;
  if (user.role === "realtor" && !pm) return <Navigate to="/realtor" replace />;
  if (pm) return ["property_manager", "realtor", "admin"].includes(user.role) ? <DisclaimerGate>{children}</DisclaimerGate> : <Navigate to={homeFor(user)} replace />;
  if (admin && user.role !== "admin") return <Navigate to={homeFor(user)} replace />;
  if (!surety && !admin && user.role === "surety_agent") return <Navigate to="/surety" replace />;
  if (surety && user.role === "mlo") return <Navigate to="/dashboard" replace />;
  return <DisclaimerGate>{children}</DisclaimerGate>;
}

function Home() {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={homeFor(user)} replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/bond/thanks" element={<BondThanks />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/r/:code" element={<Respond />} />
          <Route path="/sign/:token" element={<SignAgreement />} />
          <Route path="/upload/:token" element={<UploadDocs />} />
          <Route path="/" element={<Home />} />
          <Route path="/payment/:result" element={<Protected any><PaymentReturn /></Protected>} />
          <Route element={<Protected surety><Layout /></Protected>}>
            <Route path="/surety" element={<SuretyRuns />} />
            <Route path="/surety/leads" element={<SuretyLeads />} />
            <Route path="/surety/profile" element={<SuretyProfile />} />
            <Route path="/surety/texts" element={<SuretyTexts />} />
            <Route path="/surety/calls" element={<SuretyCalls />} />
            <Route path="/surety/appointments" element={<SuretyAppointments />} />
          </Route>
          <Route element={<Protected pm><Layout /></Protected>}>
            <Route path="/pm" element={<PmLeads kind="owner" />} />
            <Route path="/pm/rentals" element={<PmLeads kind="agent" />} />
            <Route path="/pm/appointments" element={<PmAppointments />} />
            <Route path="/pm/emails" element={<PmEmails />} />
            <Route path="/pm/call-recordings" element={<PmRecordings />} />
            <Route path="/pm/texts" element={<PmTexts />} />
            <Route path="/realtor" element={<PmLeads kind="fsbo" />} />
            <Route path="/realtor/expired" element={<PmLeads kind="expired" />} />
            <Route path="/realtor/reo" element={<RtReo />} />
            <Route path="/realtor/payments" element={<RtPayments />} />
            <Route path="/realtor/reverse" element={<ReverseProspecting />} />
            <Route path="/pm/reverse" element={<ReverseProspecting />} />
            <Route path="/pm/market" element={<PmMarket />} />
            <Route path="/realtor/referrals" element={<Referrals />} />
          </Route>
          <Route element={<Protected><Layout /></Protected>}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/referrals" element={<Referrals />} />
            <Route path="/new-run" element={<NewRun />} />
            <Route path="/prospects" element={<Prospects />} />
            <Route path="/leads" element={<Leads />} />
            <Route path="/communications" element={<CommunicationsHub />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/calls" element={<Navigate to="/communications?tab=calls" replace />} />
            <Route path="/marketplace" element={<Marketplace />} />
            <Route path="/wallet" element={<LeadWallet />} />
            <Route path="/appointments" element={<Appointments />} />
            <Route path="/texts" element={<Texts />} />
            <Route path="/integrations" element={<Integrations />} />
          </Route>
          <Route element={<Protected admin><Layout /></Protected>}>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/settings" element={<AdminSettings />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/templates" element={<AdminTemplates />} />
            <Route path="/admin/suppression" element={<AdminSuppression />} />
            <Route path="/admin/audit" element={<AdminAudit />} />
            <Route path="/admin/api-usage" element={<AdminApiUsage />} />
            <Route path="/admin/stripe" element={<AdminStripe />} />
            <Route path="/admin/postcards" element={<AdminPostcards />} />
            <Route path="/admin/calls" element={<CallLog />} />
            <Route path="/admin/call-recordings" element={<AdminRecordings />} />
            <Route path="/admin/appointments" element={<Appointments />} />
            <Route path="/admin/ai-script" element={<AdminAiScript />} />
            <Route path="/admin/text-script" element={<AdminTextScript />} />
            <Route path="/admin/texts" element={<Texts />} />
            <Route path="/admin/deity" element={<AdminDeitySubs />} />
            <Route path="/admin/astoria" element={<AdminAstoria />} />
            <Route path="/admin/marketplace" element={<AdminMarketplace />} />
            <Route path="/admin/live-transfers" element={<AdminLiveTransfers />} />
            <Route path="/admin/surety" element={<AdminSurety />} />
            <Route path="/admin/property-management" element={<AdminPM />} />
            <Route path="/admin/realtors" element={<AdminRealtors />} />
            <Route path="/admin/pm-market" element={<AdminPmMarket />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
